/**
 * Theme Configuration
 * Main theme export for the Mensova application
 * Combines palette, typography, shadows, and component overrides
 */

import { createTheme, ThemeOptions } from '@mui/material/styles';
import { darkPalette } from './palette';
import { typography } from './typography';
import { shadows } from './shadows';
import { components, spacing } from './components';

/**
 * Shared theme configuration
 * Common settings shared across theme variants
 */
const baseTheme: ThemeOptions = {
  typography,
  shadows,
  components,
  spacing,
  shape: {
    borderRadius: 8, // Default border radius
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
  },
  zIndex: {
    mobileStepper: 1000,
    fab: 1050,
    speedDial: 1050,
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
  },
};

/**
 * Theme — dark mode only for MVP
 * Light mode can be added later by re-introducing lightPalette
 */
export const theme = createTheme({
  ...baseTheme,
  palette: darkPalette,
});

/**
 * Export theme utilities
 */
export { darkPalette } from './palette';
export { scoreColors, statusColors, trendColors } from './palette';
export { typography } from './typography';
export { shadows, elevation, customShadows } from './shadows';
export { components, borderRadius, spacing } from './components';
export * from './skill-colors';
export {
  focusStyles,
  focusOutline,
  focusInset,
  focusRing,
  focusHighlight,
  createFocusStyles,
  globalFocusCSS,
  FOCUS_COLOR,
  FOCUS_WIDTH,
  FOCUS_OFFSET,
} from './focusStyles';

export {
  MIN_TOUCH_TARGET,
  MIN_TOUCH_SPACING,
  MIN_TOUCH_TARGET_AA,
  touchTargetStyles,
  expandedTouchTarget,
  touchTargetWithSpacing,
  iconButtonTouchTarget,
  compactTouchTarget,
  linkTouchTarget,
  inputTouchTarget,
  controlTouchTarget,
  closeButtonTouchTarget,
  buttonGroupSpacing,
  navItemTouchTarget,
  listItemTouchTarget,
  tabTouchTarget,
  chipTouchTarget,
  createTouchTarget,
  validateTouchTarget,
  validateTouchSpacing,
} from './touchTargets';

// Animation configuration
export {
  DURATION,
  EASING,
  transitions,
  animationStyles,
  interactionStyles,
  pageTransitionStyles,
  staggerDelay,
  createAnimation,
  // Keyframes
  shimmer,
  fadeIn,
  fadeOut,
  slideInUp,
  slideInDown,
  slideInLeft,
  slideInRight,
  scaleIn,
  pulse,
  spin,
  shake,
} from './animations';

// UI Consistency standards
export {
  SPACING,
  PAGE_SPACING,
  CARD_SPACING,
  FORM_SPACING,
  ANIMATION_STAGGER_DELAY,
  TYPOGRAPHY_USAGE,
  BUTTON_SIZES,
  ICON_SIZES,
  ELEVATION,
  BORDER_RADIUS,
  BREAKPOINTS,
  Z_INDEX,
  cardStyles,
  pageContainerStyles,
  flexRow,
  flexColumn,
  formFieldWrapper,
  actionButtonRow,
} from './uiConsistency';

/**
 * Type augmentation for custom theme properties
 * Extends the MUI theme with Mensova-specific properties
 */
declare module '@mui/material/styles' {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface Theme {
    // Custom properties can be added here if needed
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface ThemeOptions {
    // Custom options can be added here if needed
  }

  interface TypographyVariants {
    displayXl: React.CSSProperties;
    displayLg: React.CSSProperties;
    dataLg: React.CSSProperties;
    dataMd: React.CSSProperties;
    dataSm: React.CSSProperties;
    code: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    displayXl?: React.CSSProperties;
    displayLg?: React.CSSProperties;
    dataLg?: React.CSSProperties;
    dataMd?: React.CSSProperties;
    dataSm?: React.CSSProperties;
    code?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    displayXl: true;
    displayLg: true;
    dataLg: true;
    dataMd: true;
    dataSm: true;
    code: true;
  }
}
